import { FALLBACK_SUBCATEGORY_ID } from './sections';
import {
  DISPLAY_NAME_BY_TAG_KEY,
  SUBCATEGORY_BY_EXACT_TAGS,
  SUBCATEGORY_BY_TAG_SUBSET,
  TAG_KEY_BY_CATEGORY_NAME,
} from './subcategoryTags.generated';

export { DISPLAY_NAME_BY_TAG_KEY, TAG_KEY_BY_CATEGORY_NAME };

/**
 * Where a document belongs in the pärm, decided from its tags (AG-249).
 *
 * The tables come from `./subcategoryTags.generated.ts`, rebuilt by
 * `npm run generate:subcategory-tags` from appendix A of the AG-249 analysis
 * (tag set and display name per former category) and the former
 * category→shelf table kept in the script. Nothing in the tables is hand-edited.
 *
 * Two lookups, in order, because the pärm's shelves were curated by topic and
 * are not expressible as tag subsets — `djur-journaler` holds both a barn
 * journal and a livestock invoice, which share only `djur`.
 *
 *   1. EXACT — the full tag set. Every curated set (the former categories plus
 *      the types added since) is unique and listed, so a document migrated from
 *      a category lands exactly where it did before, Övrigt included.
 *   2. SUBSET — for tag combinations no category ever had, which the tag picker
 *      now makes possible. A shelf takes part when its members share at least
 *      two tags that no other shelf's member carries; a lone AREA or KIND tag is
 *      too weak a signal (there is no bare `garden`, `plan` or `arbetsmiljo` rule).
 *
 * Anything else falls back to "Övrigt".
 */

/**
 * The subcategory a document belongs to, from its tags.
 *
 * @param tags Tag slugs carried by the document.
 * @returns A subcategory id, or the "Övrigt" fallback when nothing matches.
 */
export function resolveSubcategoryId(tags: readonly string[] | null | undefined): string {
  if (!tags || tags.length === 0) return FALLBACK_SUBCATEGORY_ID;

  const exact = SUBCATEGORY_BY_EXACT_TAGS[tagKey(tags)];
  if (exact) return exact;

  // Most specific first, so a spray journal is not claimed by a broader entry.
  const carried = new Set(tags);
  const match = SUBCATEGORY_BY_TAG_SUBSET.find((entry) =>
    entry.tags.every((tag) => carried.has(tag)),
  );

  return match?.id ?? FALLBACK_SUBCATEGORY_ID;
}

/**
 * The tag set that files a document under `subcategoryId` by the subset rule —
 * the preset for an upload started from a "Brukar efterfrågas" suggestion that
 * names no former category. Null when the subcategory has no subset rule, in
 * which case the farmer picks the tags and the pärm places the document by them.
 *
 * @param subcategoryId A pärm subcategory id.
 * @returns The rule's tag slugs, or null.
 */
export function defaultTagsForSubcategory(subcategoryId: string): string[] | null {
  const rule = SUBCATEGORY_BY_TAG_SUBSET.find((entry) => entry.id === subcategoryId);
  return rule ? [...rule.tags] : null;
}

/**
 * The pärm title for a curated tag set — the former category's display name —
 * or null for a set that never was a category.
 *
 * @param tags Tag slugs, in any order.
 * @returns The title, or null.
 */
export function documentTypeTitle(tags: readonly string[] | null | undefined): string | null {
  const key = tagKey(tags);
  return key === '' ? null : (DISPLAY_NAME_BY_TAG_KEY[key] ?? null);
}

/** Canonical key for a document's tag set — sorted, so order cannot split it. */
export function tagKey(tags: readonly string[] | null | undefined): string {
  return !tags || tags.length === 0 ? '' : [...tags].sort().join('|');
}

/**
 * The tag set a `tagKey` stands for.
 *
 * The pärm hands its group keys to the upload dialog as the preset tags; this
 * is that step. It must never be confused with `TAG_KEY_BY_CATEGORY_NAME`,
 * which is keyed by the OLD category names — looking a tag key up there
 * silently yields nothing, and the upload lands untagged.
 *
 * @param key A `tagKey` value; the empty key is the untagged set.
 * @returns The slugs, in key order.
 */
export function tagsFromKey(key: string): string[] {
  return key === '' ? [] : key.split('|');
}

/**
 * A document's tag sets ("taggrupper") — the one place the frontend decides
 * what sets a document has. Consumers that place, match or label a document read
 * this, never `doc.tags` (the union), so a document on two shelves cannot be
 * mistaken for one set carrying both groups' tags.
 *
 * Falls back to `[tags]` when the API predates tag groups (deploy window) and
 * drops empty sets and duplicate sets. An empty result means "Okategoriserad".
 *
 * @param doc Anything carrying `tags` and optionally `tagSets`.
 * @returns The distinct, non-empty sets in the document's own order.
 */
export function documentTagSets(doc: {
  tags?: readonly string[] | null | undefined;
  tagSets?: readonly (readonly string[])[] | null | undefined;
}): string[][] {
  const raw = doc.tagSets?.length ? doc.tagSets : doc.tags?.length ? [doc.tags] : [];
  const seen = new Set<string>();
  const sets: string[][] = [];
  for (const set of raw) {
    const key = tagKey(set);
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    sets.push([...set]);
  }
  return sets;
}
